import React, { useEffect, useState } from 'react';
import { StyleSheet, TouchableOpacity, View, ScrollView, Image, useWindowDimensions, ActivityIndicator, Alert, Platform, Modal } from 'react-native';
import { Text } from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import { useQueryClient } from '@tanstack/react-query';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import { useAppColors } from '../../hooks/useAppColors';
import { WEB_MAX_WIDTH } from '../../hooks/useResponsiveLayout';
import { QUERY_KEYS } from '../../utils/constants';
import { formatCurrency } from '../../utils/formatters';
import { useAuthStore } from '../../stores/authStore';
import { paymentsApi } from '../../api/payments';
import { useMarkVietQRPaid } from '../../hooks/usePayments';
import { usePaymentVerificationStream } from '../../hooks/usePaymentVerificationStream';
import { useTranslation } from 'react-i18next';
import type { RootStackParamList } from '../../navigation/types';

type VietQRRoute = RouteProp<RootStackParamList, 'VietQR'>;
type Phase = 'awaiting_user' | 'user_confirmed' | 'admin_verified' | 'failed';

export default function VietQRScreen() {
  const { t } = useTranslation();
  const colors = useAppColors();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();
  const route = useRoute<VietQRRoute>();
  const queryClient = useQueryClient();
  const setNeedsSubscription = useAuthStore((s) => s.setNeedsSubscription);
  const needsSubscription = useAuthStore((s) => s.needsSubscription);
  const [initialNeedsSubscription] = useState(() => useAuthStore.getState().needsSubscription);
  const setActiveVietQRPayment = useAuthStore((s) => s.setActiveVietQRPayment);
  const setPendingVerificationPaymentId = useAuthStore((s) => s.setPendingVerificationPaymentId);
  const pendingVerificationPaymentId = useAuthStore((s) => s.pendingVerificationPaymentId);
  const setReusablePaymentId = useAuthStore((s) => s.setReusablePaymentId);
  const clearSubscriptionForm = useAuthStore((s) => s.clearSubscriptionForm);
  const setSubscriptionForm = useAuthStore((s) => s.setSubscriptionForm);

  const { payment_id, order_number, amount, qr_url, bank_bin, account_number, account_name, order_items } = route.params;
  const { width: screenWidth } = useWindowDimensions();
  const isWide = screenWidth > WEB_MAX_WIDTH;

  const isAlreadySubmitted = pendingVerificationPaymentId === payment_id;
  const forceShowQr = route.params.showQr;
  const [phase, setPhase] = useState<Phase>(isAlreadySubmitted && !forceShowQr ? 'user_confirmed' : 'awaiting_user');
  const [hasMarkedPaid, setHasMarkedPaid] = useState(isAlreadySubmitted);
  const [isSavingQR, setIsSavingQR] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const markPaidMutation = useMarkVietQRPaid();

  const { lastResult: sseResult } = usePaymentVerificationStream(
    phase === 'awaiting_user' || phase === 'user_confirmed' ? payment_id : null
  );

  // ─── Check payment status on mount (refresh recovery) ────────────────
  useEffect(() => {
    let cancelled = false;
    paymentsApi.getVietQRPaymentStatus(payment_id).then((res) => {
      if (cancelled) return;
      if (res.status === 'completed') {
        setPhase('admin_verified');
        setNeedsSubscription(false);
        setActiveVietQRPayment(null);
        setPendingVerificationPaymentId(null);
        setReusablePaymentId(null);
        clearSubscriptionForm();
        queryClient.invalidateQueries();
      } else if (res.status === 'failed' || res.status === 'cancelled' || res.status === 'expired') {
        setPhase('failed');
        setActiveVietQRPayment(null);
        setPendingVerificationPaymentId(null);
        setReusablePaymentId(null);
      } else if (res.user_marked_paid_at) {
        if (!forceShowQr) {
          setPhase('user_confirmed');
        }
        setHasMarkedPaid(true);
      }
    }).catch(() => {
      // Ignore errors — SSE listener will handle it
    });
    return () => { cancelled = true; };
  }, [payment_id, forceShowQr, setNeedsSubscription, setActiveVietQRPayment, setPendingVerificationPaymentId, setReusablePaymentId, queryClient, clearSubscriptionForm]);

  // ─── SSE result handler ──────────────────────────────────────────────
  useEffect(() => {
    if (!sseResult) return;
    if (sseResult.status === 'completed') {
      setPhase('admin_verified');
      setNeedsSubscription(false);
      setActiveVietQRPayment(null);
      setPendingVerificationPaymentId(null);
      setReusablePaymentId(null);
      clearSubscriptionForm();
      queryClient.invalidateQueries();
      // Legal status is re-validated by MainTabNavigator when the user lands on Main.
    }
  }, [sseResult, setNeedsSubscription, setActiveVietQRPayment, setPendingVerificationPaymentId, setReusablePaymentId, queryClient, clearSubscriptionForm]);

  const handleMarkPaid = async () => {
    try {
      await markPaidMutation.mutateAsync(payment_id);
      setPhase('user_confirmed');
      setHasMarkedPaid(true);
      setPendingVerificationPaymentId(payment_id);
    } catch (err) {
      console.warn('Mark paid failed:', err);
    }
  };

  const handleContinueToApp = () => {
    // MainTabNavigator re-validates legal status on focus, so no manual check here.
    (navigation as any).reset({ index: 0, routes: [{ name: 'Main' }] });
  };

  const handleDone = () => {
    setActiveVietQRPayment(null);
    setPendingVerificationPaymentId(null);
    setReusablePaymentId(null);
    // MainTabNavigator re-validates legal status on focus, so no manual check here.
    (navigation as any).reset({ index: 0, routes: [{ name: 'Main' }] });
  };

  const handleSaveQR = async () => {
    if (isSavingQR) return;
    setIsSavingQR(true);
    try {
      if (Platform.OS === 'web') {
        try {
          const response = await fetch(qr_url);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `vietqr-${order_number}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (err) {
          window.open(qr_url, '_blank');
        }
        return;
      }

      // Native iOS / Android flow — download to cache then share (no media library permission needed)
      const filename = `vietqr-${order_number}.png`;
      const fileUri = `${(FileSystem as any).cacheDirectory}${filename}`;
      const result = await FileSystem.downloadAsync(qr_url, fileUri);
      const isAvailable = await Sharing.isAvailableAsync();
      if (!isAvailable) {
        Alert.alert(
          t('common.error', 'Error'),
          t('vietqr.qrSavedFailed', 'Failed to save the QR code. Please take a screenshot instead.')
        );
        return;
      }
      await Sharing.shareAsync(result.uri, { mimeType: 'image/png', dialogTitle: `vietqr-${order_number}` });
    } catch (err) {
      console.error('Failed to save QR:', err);
      Alert.alert(
        t('common.error', 'Error'),
        t('vietqr.qrSavedFailed', 'Failed to save the QR code. Please take a screenshot instead.')
      );
    } finally {
      setIsSavingQR(false);
    }
  };

  const handleCancel = () => {
    setShowCancelDialog(true);
  };

  const performCancel = async () => {
    setShowCancelDialog(false);
    try {
      await paymentsApi.cancelPayment(payment_id);
    } catch (err) {
      console.error('Failed to cancel payment:', err);
    }

    // Explicitly set the step to 2 in the persisted store before resetting/going back
    const currentForm = useAuthStore.getState().subscriptionForm;
    if (currentForm) {
      setSubscriptionForm({
        ...currentForm,
        step: 2,
      });
    }

    setActiveVietQRPayment(null);
    setPendingVerificationPaymentId(null);
    // Remember this (now cancelled) payment so the next Pay click updates it
    // in-place instead of creating a brand-new order.
    setReusablePaymentId(payment_id);

    // Only keep/set needsSubscription to true if they actually needed one (onboarding)
    if (initialNeedsSubscription) {
      setNeedsSubscription(true);
    }

    // Defer navigation until the next tick so the state updates commit first,
    // allowing RootNavigator to swap the active screen configuration.
    setTimeout(() => {
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else {
        if (initialNeedsSubscription) {
          (navigation as any).reset({ index: 0, routes: [{ name: 'Subscription' }] });
        } else {
          (navigation as any).reset({ index: 0, routes: [{ name: 'Main' }] });
        }
      }
    }, 0);
  };

  const isAwaiting = phase === 'awaiting_user';
  const isUserConfirmed = phase === 'user_confirmed';
  const isAdminVerified = phase === 'admin_verified';
  const isFailed = phase === 'failed';

  // ─── Status header ──────────────────────────────────────────────
  const statusHeader = (
    <>
      {isAwaiting && (
        <>
          <View style={styles.titleRow}>
            <Icon name="qrcode-scan" size={32} color={colors.accent} />
            <Text style={[styles.title, { color: colors.text }]}>{t('vietqr.awaitingTitle')}</Text>
          </View>
          <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
            {t('vietqr.awaitingSubtitle')}
          </Text>
        </>
      )}

      {isUserConfirmed && (
        <>
          <View style={styles.titleRow}>
            <Icon name="check-circle" size={32} color={colors.success} />
            <Text style={[styles.title, { color: colors.text }]}>{t('vietqr.confirmedTitle')}</Text>
          </View>
          <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
            {t('vietqr.confirmedSubtitle')}
          </Text>
        </>
      )}

      {isAdminVerified && (
        <>
          <View style={styles.titleRow}>
            <Icon name="check-circle" size={32} color={colors.success} />
            <Text style={[styles.title, { color: colors.text }]}>{t('vietqr.successTitle')}</Text>
          </View>
          <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
            {t('vietqr.successSubtitle')}
          </Text>
        </>
      )}

      {isFailed && (
        <>
          <View style={styles.titleRow}>
            <Icon name="close-circle" size={32} color={colors.error} />
            <Text style={[styles.title, { color: colors.text }]}>{t('vietqr.failedTitle')}</Text>
          </View>
          <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
            {t('vietqr.failedSubtitle')}
          </Text>
        </>
      )}
    </>
  );

  // ─── QR section (only while awaiting user) ──────────────────────
  const qrSection = isAwaiting ? (
    <View style={[styles.qrCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
      <Text style={[styles.qrInstruction, { color: colors.text }]}>
        {t('vietqr.scanToPay')}
      </Text>

      <View style={styles.qrWrapper}>
        <Image
          source={{ uri: qr_url }}
          style={{ width: isWide ? 240 : 220, height: isWide ? 240 : 220 }}
          resizeMode="contain"
        />
      </View>

      {/* Save QR Button */}
      <TouchableOpacity
        style={[styles.saveQrBtn, { borderColor: colors.border }]}
        activeOpacity={0.7}
        onPress={handleSaveQR}
        disabled={isSavingQR}
      >
        {isSavingQR ? (
          <ActivityIndicator size="small" color={colors.accent} />
        ) : (
          <View style={styles.saveQrBtnContent}>
            <Icon name="download" size={18} color={colors.accent} />
            <Text style={[styles.saveQrBtnText, { color: colors.accent }]}>
              {t('vietqr.saveQrCode', 'Save QR Code')}
            </Text>
          </View>
        )}
      </TouchableOpacity>

      <View style={[styles.bankDetails, { borderTopColor: colors.border }]}>
        <View style={styles.bankRow}>
          <Text style={[styles.bankLabel, { color: colors.textSecondary }]}>{t('vietqr.accountName')}</Text>
          <Text style={[styles.bankValue, { color: colors.text }]}>{account_name}</Text>
        </View>
        <View style={styles.bankRow}>
          <Text style={[styles.bankLabel, { color: colors.textSecondary }]}>{t('vietqr.accountNumber')}</Text>
          <Text style={[styles.bankValue, { color: colors.text }]}>{account_number}</Text>
        </View>
        <View style={styles.bankRow}>
          <Text style={[styles.bankLabel, { color: colors.textSecondary }]}>{t('vietqr.bankBin')}</Text>
          <Text style={[styles.bankValue, { color: colors.text }]}>{bank_bin}</Text>
        </View>
      </View>
    </View>
  ) : null;

  // ─── Order summary ──────────────────────────────────────────────
  const summarySection = (
    <View style={[styles.summaryCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
      <View style={styles.summaryRow}>
        <Text style={[styles.summaryLabel, { color: colors.textSecondary }]}>{t('vietqr.orderNumber')}</Text>
        <Text style={[styles.summaryValue, { color: colors.text }]}>{order_number}</Text>
      </View>
      <View style={[styles.summaryDivider, { borderBottomColor: colors.border }]} />
      <View style={styles.summaryRow}>
        <Text style={[styles.summaryLabel, { color: colors.textSecondary }]}>{t('vietqr.amount')}</Text>
        <Text style={[styles.summaryValue, { color: colors.text }]}>
          {formatCurrency(typeof amount === 'string' ? parseFloat(amount) : amount)}
        </Text>
      </View>
      <View style={[styles.summaryDivider, { borderBottomColor: colors.border }]} />
      <View style={styles.summaryRow}>
        <Text style={[styles.summaryLabel, { color: colors.textSecondary }]}>{t('vietqr.status')}</Text>
        <View style={styles.statusRow}>
          {isAwaiting && (
            <>
              <View style={[styles.statusDot, { backgroundColor: colors.warning }]} />
              <Text style={[styles.summaryValue, { color: colors.warning }]}>
                {hasMarkedPaid ? t('vietqr.statusAwaitingVerification', 'Awaiting Verification') : t('vietqr.statusPending')}
              </Text>
            </>
          )}
          {isUserConfirmed && (
            <>
              <View style={[styles.statusDot, { backgroundColor: colors.warning }]} />
              <Text style={[styles.summaryValue, { color: colors.warning }]}>
                {t('vietqr.statusAwaitingVerification', 'Awaiting Verification')}
              </Text>
            </>
          )}
          {isAdminVerified && (
            <>
              <View style={[styles.statusDot, { backgroundColor: colors.success }]} />
              <Text style={[styles.summaryValue, { color: colors.success }]}>{t('vietqr.statusCompleted')}</Text>
            </>
          )}
          {isFailed && (
            <>
              <View style={[styles.statusDot, { backgroundColor: colors.error }]} />
              <Text style={[styles.summaryValue, { color: colors.error }]}>{t('vietqr.statusFailed')}</Text>
            </>
          )}
        </View>
      </View>
    </View>
  );

  const orderItemsParsed = (order_items && order_items.length > 0 ? order_items : []).map((item) => ({
    label: item.label,
    description: item.description ? t(`plans.${item.description}.name`, { defaultValue: item.description }) : '',
    subtotal: typeof item.subtotal === 'string' ? parseFloat(item.subtotal) : item.subtotal,
    discount_amount: typeof item.discount_amount === 'string' ? parseFloat(item.discount_amount) : item.discount_amount,
    final_amount: typeof item.final_amount === 'string' ? parseFloat(item.final_amount) : item.final_amount,
    term_months: item.term_months,
  }));
  const hasAnyDiscount = orderItemsParsed.some((i) => i.discount_amount > 0);
  const subtotalSum = orderItemsParsed.reduce((sum, i) => sum + i.subtotal, 0);
  const discountSum = orderItemsParsed.reduce((sum, i) => sum + i.discount_amount, 0);
  const authoritative_amount = typeof amount === 'string' ? parseFloat(amount) : amount;

  // ─── Order details ────────────────────────────────────────────────
  const orderDetailsSection = (isAwaiting || isUserConfirmed) ? (
    <View style={[styles.orderDetailsCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
      <View style={styles.orderDetailsHeader}>
        <Icon name="receipt-text-outline" size={18} color={colors.accent} />
        <Text style={[styles.orderDetailsTitle, { color: colors.text }]}>{t('vietqr.orderDetails')}</Text>
      </View>

      <View style={[styles.orderDetailsDivider, { borderBottomColor: colors.border }]} />

      {orderItemsParsed.length > 0 ? orderItemsParsed.map((item, index) => {
        const itemTerm = item.term_months || 1;
        return (
          <View key={`${item.label}-${index}`} style={styles.orderItemRow}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={[styles.orderItemLabel, { color: colors.text }]} numberOfLines={1}>
                {item.label}
              </Text>
              {!!item.description && (
                <Text style={[styles.orderItemDescription, { color: colors.textSecondary }]} numberOfLines={1}>
                  {item.description}
                </Text>
              )}
              <Text style={[styles.orderItemCalc, { color: colors.textSecondary, fontSize: 11, marginTop: 2 }]}>
                {formatCurrency(item.subtotal / itemTerm)}/mo × {itemTerm} {itemTerm === 1 ? t('vietqr.month', 'month') : t('vietqr.months', 'months')}
              </Text>
            </View>
            <View style={{ alignItems: 'flex-end' }}>
              {item.discount_amount > 0 && (
                <Text style={[styles.orderItemAmountStrike, { color: colors.textSecondary }]}>
                  {formatCurrency(item.subtotal)}
                </Text>
              )}
              <Text style={[styles.orderItemAmount, { color: colors.text }]}>
                {formatCurrency(item.final_amount)}
              </Text>
            </View>
          </View>
        );
      }) : (
        <View style={styles.orderItemRow}>
          <Text style={[styles.orderItemLabel, { color: colors.text }]}>{t('vietqr.membershipOrder')}</Text>
          <Text style={[styles.orderItemAmount, { color: colors.text }]}>
            {formatCurrency(authoritative_amount)}
          </Text>
        </View>
      )}

      <View style={[styles.orderDetailsDivider, { borderBottomColor: colors.border }]} />

      {hasAnyDiscount && (
        <>
          <View style={styles.orderTotalRow}>
            <Text style={[styles.orderSubtotalLabel, { color: colors.textSecondary }]}>{t('vietqr.subtotal')}</Text>
            <Text style={[styles.orderSubtotalAmount, { color: colors.textSecondary }]}>
              {formatCurrency(subtotalSum)}
            </Text>
          </View>
          <View style={[styles.orderTotalRow, { marginTop: 4 }]}>
            <Text style={[styles.orderDiscountLabel, { color: colors.accent }]}>{t('vietqr.discounts')}</Text>
            <Text style={[styles.orderDiscountAmount, { color: colors.accent }]}>
              -{formatCurrency(discountSum)}
            </Text>
          </View>
          <View style={[styles.orderDetailsDivider, { borderBottomColor: colors.border, marginTop: 8 }]} />
        </>
      )}

      <View style={styles.orderTotalRow}>
        <Text style={[styles.orderTotalLabel, { color: colors.text }]}>{t('vietqr.total')}</Text>
        <Text style={[styles.orderTotalAmount, { color: colors.text }]}>
          {formatCurrency(authoritative_amount)}
        </Text>
      </View>
    </View>
  ) : null;

  return (
    <View style={[styles.screen, { backgroundColor: colors.bg, paddingTop: insets.top }]}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[styles.scrollContent, { paddingBottom: insets.bottom + 24 }]}
        showsVerticalScrollIndicator={false}
      >
        {statusHeader}

        {isWide && isAwaiting ? (
          <View style={styles.wideColumns}>
            <View style={styles.wideLeft}>
              {qrSection}
            </View>
            <View style={styles.wideRight}>
              {summarySection}
              {orderDetailsSection}
            </View>
          </View>
        ) : (
          <>
            {qrSection}
            {summarySection}
            {orderDetailsSection}
          </>
        )}

        <View style={[styles.actions, isWide && styles.actionsWide]}>
          {isAwaiting && (
            <>
              {!hasMarkedPaid ? (
                <TouchableOpacity
                  style={[styles.primaryBtn, { backgroundColor: colors.accent }]}
                  activeOpacity={0.8}
                  onPress={handleMarkPaid}
                  disabled={markPaidMutation.isPending}
                >
                  <Text style={[styles.primaryBtnText, { color: colors.accentText }]}>
                    {markPaidMutation.isPending ? t('vietqr.markingPaid') : t('vietqr.markPaidBtn')}
                  </Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={[styles.primaryBtn, { backgroundColor: colors.accent }]}
                  activeOpacity={0.8}
                  onPress={handleContinueToApp}
                >
                  <View style={styles.primaryBtnWithIcon}>
                    <Icon name="check-circle" size={20} color={colors.accentText} />
                    <Text style={[styles.primaryBtnText, { color: colors.accentText }]}>
                      {t('vietqr.continueToApp', 'Continue to app')}
                    </Text>
                  </View>
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={[styles.secondaryBtn, { backgroundColor: colors.card, borderColor: colors.border }]}
                activeOpacity={0.8}
                onPress={handleCancel}
              >
                <Text style={[styles.secondaryBtnText, { color: colors.text }]}>
                  {t('vietqr.cancelPayment', 'Cancel Payment')}
                </Text>
              </TouchableOpacity>
            </>
          )}

          {isUserConfirmed && (
            <>
              <Text style={[styles.continueNote, { color: colors.textSecondary, textAlign: 'center' }]}>
                {t('vietqr.confirmedContinueNote')}
              </Text>
              <TouchableOpacity
                style={[styles.primaryBtn, { backgroundColor: colors.accent }]}
                activeOpacity={0.8}
                onPress={handleContinueToApp}
              >
                <Text style={[styles.primaryBtnText, { color: colors.accentText }]}>{t('vietqr.continueToApp')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.linkBtn, { marginTop: 8 }]}
                activeOpacity={0.7}
                onPress={() => setPhase('awaiting_user')}
              >
                <Text style={[styles.linkBtnText, { color: colors.accent }]}>
                  {t('vietqr.backToPaymentInstructions', 'View QR Code & Bank Transfer Details')}
                </Text>
              </TouchableOpacity>
            </>
          )}

          {isAdminVerified && (
            <TouchableOpacity
              style={[styles.primaryBtn, { backgroundColor: colors.accent }]}
              activeOpacity={0.8}
              onPress={handleDone}
            >
              <Text style={[styles.primaryBtnText, { color: colors.accentText }]}>{t('common.done')}</Text>
            </TouchableOpacity>
          )}

          {isFailed && (
            <TouchableOpacity
              style={[styles.primaryBtn, { backgroundColor: colors.accent }]}
              activeOpacity={0.8}
              onPress={() => {
                if (navigation.canGoBack()) {
                  navigation.goBack();
                } else {
                  if (initialNeedsSubscription) {
                    (navigation as any).reset({ index: 0, routes: [{ name: 'Subscription' }] });
                  } else {
                    (navigation as any).reset({ index: 0, routes: [{ name: 'Main' }] });
                  }
                }
              }}
            >
              <Text style={[styles.primaryBtnText, { color: colors.accentText }]}>{t('vietqr.tryAgain')}</Text>
            </TouchableOpacity>
          )}
        </View>
      </ScrollView>

      {/* Custom Cancel Confirmation Dialog */}
      <Modal
        visible={showCancelDialog}
        transparent
        animationType="fade"
        onRequestClose={() => setShowCancelDialog(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowCancelDialog(false)}
        >
          <TouchableOpacity
            style={[styles.modalCard, { backgroundColor: colors.card, borderColor: colors.border }]}
            activeOpacity={1}
          >
            <View style={[styles.modalIconContainer, { backgroundColor: colors.error + '15' }]}>
              <Icon name="alert-circle-outline" size={32} color={colors.error} />
            </View>
            
            <Text style={[styles.modalTitle, { color: colors.text }]}>
              {t('vietqr.cancelConfirmTitle', 'Cancel Payment?')}
            </Text>
            
            <Text style={[styles.modalDesc, { color: colors.textSecondary }]}>
              {t('vietqr.cancelConfirmDesc', 'Are you sure you want to cancel this payment? This invoice and QR code will be permanently invalidated, and you will need to restart the checkout process.')}
            </Text>
            
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={[styles.modalDestructiveBtn, { backgroundColor: colors.error }]}
                activeOpacity={0.8}
                onPress={performCancel}
              >
                <Text style={[styles.modalDestructiveBtnText, { color: '#ffffff' }]}>
                  {t('vietqr.cancelConfirmBtn', 'Yes, Cancel Payment')}
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[styles.modalCancelBtn, { backgroundColor: colors.card, borderColor: colors.border }]}
                activeOpacity={0.8}
                onPress={() => setShowCancelDialog(false)}
              >
                <Text style={[styles.modalCancelBtnText, { color: colors.text }]}>
                  {t('vietqr.keepPaymentBtn', 'No, Keep Payment')}
                </Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  scrollView: { flex: 1 },
  scrollContent: {
    padding: 20,
    gap: 16,
    alignItems: 'center',
    maxWidth: WEB_MAX_WIDTH,
    width: '100%',
    alignSelf: 'center',
  },

  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  title: { fontSize: 24, fontWeight: '800', textAlign: 'center', marginBottom: 8 },
  subtitle: { fontSize: 14, textAlign: 'center', lineHeight: 22, marginBottom: 8, paddingHorizontal: 16 },
  continueNote: { fontSize: 13, textAlign: 'center', lineHeight: 20, marginBottom: 12 },

  wideColumns: {
    flexDirection: 'row',
    gap: 20,
    width: '100%',
  },
  wideLeft: {
    flex: 1,
    minWidth: 0,
  },
  wideRight: {
    flex: 1,
    gap: 16,
    minWidth: 0,
  },

  qrCard: {
    width: '100%',
    borderRadius: 24,
    borderWidth: 1,
    padding: 24,
    alignItems: 'center',
  },
  qrWrapper: {
    padding: 16,
    backgroundColor: '#fff',
    borderRadius: 16,
    marginBottom: 16,
  },
  qrInstruction: {
    fontSize: 15,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 16,
  },
  bankDetails: {
    width: '100%',
    borderTopWidth: 1,
    paddingTop: 16,
    gap: 8,
  },
  bankRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  bankLabel: { fontSize: 13 },
  bankValue: { fontSize: 14, fontWeight: '700' },

  summaryCard: {
    width: '100%',
    borderRadius: 16,
    borderWidth: 1,
    padding: 20,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
  },
  summaryDivider: { borderBottomWidth: 1 },
  summaryLabel: { fontSize: 13 },
  summaryValue: { fontSize: 14, fontWeight: '700' },
  statusRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  statusDot: { width: 8, height: 8, borderRadius: 4 },

  orderDetailsCard: {
    width: '100%',
    borderRadius: 16,
    borderWidth: 1,
    padding: 16,
  },
  orderDetailsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  orderDetailsTitle: { fontSize: 14, fontWeight: '800' },
  orderDetailsDivider: { borderBottomWidth: 1, marginVertical: 12 },
  orderItemRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    paddingVertical: 6,
  },
  orderItemLabel: { fontSize: 14, fontWeight: '700' },
  orderItemDescription: { fontSize: 12, marginTop: 2 },
  orderItemCalc: { fontSize: 11, marginTop: 2 },
  orderItemAmount: { fontSize: 14, fontWeight: '700' },
  orderItemAmountStrike: { fontSize: 11, textDecorationLine: 'line-through' },
  orderTotalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  orderSubtotalLabel: { fontSize: 13 },
  orderSubtotalAmount: { fontSize: 13 },
  orderDiscountLabel: { fontSize: 13, fontWeight: '600' },
  orderDiscountAmount: { fontSize: 13, fontWeight: '600' },
  orderTotalLabel: { fontSize: 14, fontWeight: '800' },
  orderTotalAmount: { fontSize: 15, fontWeight: '800' },

  actions: { width: '100%', gap: 12 },
  actionsWide: { maxWidth: 320, alignSelf: 'center' },
  primaryBtn: {
    paddingVertical: 16,
    borderRadius: 14,
    alignItems: 'center',
  },
  primaryBtnText: { fontSize: 16, fontWeight: '800' },
  primaryBtnWithIcon: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  secondaryBtn: {
    paddingVertical: 16,
    borderRadius: 14,
    alignItems: 'center',
    borderWidth: 1,
  },
  secondaryBtnText: { fontSize: 16, fontWeight: '700' },
  saveQrBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 16,
    width: '80%',
  },
  saveQrBtnContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  saveQrBtnText: {
    fontSize: 14,
    fontWeight: '700',
  },
  linkBtn: {
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  linkBtnText: {
    fontSize: 14,
    fontWeight: '700',
    textDecorationLine: 'underline',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalCard: {
    width: '100%',
    maxWidth: 340,
    borderRadius: 20,
    borderWidth: 1,
    padding: 24,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 5,
  },
  modalIconContainer: {
    marginBottom: 16,
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '800',
    textAlign: 'center',
    marginBottom: 10,
  },
  modalDesc: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
  },
  modalActions: {
    width: '100%',
    gap: 12,
  },
  modalDestructiveBtn: {
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
    width: '100%',
  },
  modalDestructiveBtnText: {
    fontSize: 15,
    fontWeight: '800',
  },
  modalCancelBtn: {
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
    borderWidth: 1,
    width: '100%',
  },
  modalCancelBtnText: {
    fontSize: 15,
    fontWeight: '700',
  },
});
